import { FC, ReactNode } from 'react';

import type { TranscriptFile, TranscriptFileKind } from '../types';

const KIND_LABEL: Record<TranscriptFileKind, string> = {
  official: 'Official',
  unofficial: 'Unofficial',
  legacy: 'Old platform document',
};

interface TranscriptFilesListProps {
  files: TranscriptFile[];
  /** Optional: only show these kinds (e.g. ['official','unofficial']). */
  kinds?: TranscriptFileKind[];
  emptyText?: string;
  className?: string;
  /**
   * Optional per-file actions rendered next to View (e.g. the admin
   * Process/Processed action on legacy documents).
   */
  renderActions?: (file: TranscriptFile) => ReactNode;
}

export const TranscriptFilesList: FC<TranscriptFilesListProps> = ({
  files: allFiles,
  kinds,
  emptyText = 'No documents attached to this transcript.',
  className,
  renderActions,
}) => {
  const files = kinds ? (allFiles ?? []).filter((f) => kinds.includes(f.kind)) : allFiles;

  if (!files || files.length === 0) {
    return (
      <p
        className={`m-0! [font-size:var(--fs-p4)]! text-(--neutral-black-NB700)! ${className ?? ''}`}
      >
        {emptyText}
      </p>
    );
  }

  return (
    <div
      className={`flex flex-col divide-y divide-(--neutral-black-NB100) rounded-[8px] border border-(--neutral-black-NB200) ${className ?? ''}`}
    >
      {files.map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
          <span className="min-w-0 flex-1 truncate [font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)!">
            {f.label ?? `${KIND_LABEL[f.kind] ?? f.kind} document`}
            <span className="ml-2 [font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
              {KIND_LABEL[f.kind] ?? f.kind}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-3">
            {renderActions?.(f)}
            {f.url ? (
              <a
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-(--primary-O900)! underline"
              >
                View
              </a>
            ) : (
              <span className="text-(--neutral-black-NB600)!">—</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};
